"use client";

import type { HTMLMotionProps } from "framer-motion";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useMessage } from "@assistant-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "@acme/ui";

import { Markdown } from "./markdown";

interface ReasoningContextType {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const ReasoningContext = createContext<ReasoningContextType | undefined>(
  undefined,
);

function useReasoningContext() {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error(
      "useReasoningContext must be used within a Reasoning provider",
    );
  }
  return context;
}

export interface ReasoningProps {
  children: React.ReactNode;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function Reasoning({
  children,
  className,
  open,
  onOpenChange,
}: ReasoningProps) {
  const [internalOpen, setInternalOpen] = useState(true);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <ReasoningContext.Provider
      value={{
        isOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <div className={className}>{children}</div>
    </ReasoningContext.Provider>
  );
}

export type ReasoningTriggerProps = {
  children: React.ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLButtonElement>;

function ReasoningTrigger({
  children,
  className,
  ...props
}: ReasoningTriggerProps) {
  const { isOpen, onOpenChange } = useReasoningContext();

  return (
    <button
      className={cn("flex cursor-pointer items-center gap-2", className)}
      onClick={() => onOpenChange(!isOpen)}
      {...props}
    >
      <span>{children}</span>
      <div
        className={cn(
          "transform transition-transform",
          isOpen ? "rotate-180" : "",
        )}
      >
        <ChevronDownIcon className="size-4" />
      </div>
    </button>
  );
}

export type ReasoningContentProps = {
  children: React.ReactNode;
  className?: string;
} & HTMLMotionProps<"div">;

function ReasoningContent({
  children,
  className,
  ...props
}: ReasoningContentProps) {
  const { isOpen } = useReasoningContext();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          {...props}
        >
          <div className={cn("mt-1 mb-3 overflow-hidden", className)}>
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export interface ReasoningResponseProps {
  text: string;
  className?: string;
  speed?: number;
  onComplete?: () => void;
  fadeDuration?: number;
  segmentDelay?: number;
  characterChunkSize?: number;
}

function ReasoningResponse({ text, className }: ReasoningResponseProps) {
  const { isOpen } = useReasoningContext();

  return (
    <div
      className={cn(
        "prose prose-sm dark:prose-invert text-muted-foreground text-sm transition-opacity duration-300 ease-out",
        isOpen ? "opacity-100" : "opacity-0",
        className,
      )}
    >
      <Markdown>{text}</Markdown>
    </div>
  );
}

export const ReasoningAssistantUICompat = ({ text }: { text: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isReasoningLastPart = useIsReasoningLastPart();

  useEffect(() => {
    if (!isReasoningLastPart) {
      setIsOpen(false);
    }
  }, [isReasoningLastPart]);

  return (
    <Reasoning open={isOpen}>
      <div className="mb-2 flex w-full flex-col">
        <ReasoningTrigger
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            isReasoningLastPart &&
              "animate-shine text-foreground/60 bg-gradient-to-r from-white/5 from-40% via-white/80 via-50% to-white/5 to-60% bg-clip-text",
            !isReasoningLastPart && "text-foreground/60",
          )}
          style={{
            backgroundSize: "200% 100%",
          }}
        >
          {isReasoningLastPart ? "Thinking" : "Finished thinking"}
        </ReasoningTrigger>
        <ReasoningContent className="ml-2 border-l-2 border-l-slate-200 px-2 pb-1 dark:border-l-slate-700">
          <ReasoningResponse text={text} />
        </ReasoningContent>
      </div>
    </Reasoning>
  );
};

function useIsReasoningLastPart() {
  const message = useMessage();

  return (
    message.content.findIndex((part) => part.type === "reasoning") ===
    message.content.length - 1
  );
}

export { Reasoning, ReasoningContent, ReasoningResponse, ReasoningTrigger };
